/**
 * Anzeigelogik fuer "wann war jemand zuletzt da" - Spalte "Letzter Login" der Nutzerverwaltung
 * (`components/Users/NutzerSeite.tsx`) und Abschnitt "Zuletzt aktiv" im Marken-Menue
 * (`components/App/MarkenMenue.tsx`). NEUENTWICKLUNG ohne Bestandsvorbild in `index.html`, deshalb
 * ein eigenes Modul statt eines Eintrags in `lib/format.ts` (selbe Begruendung wie
 * `lib/datumsAnzeige.ts`: `scripts/gen-herkunft.ts` gleicht `lib/format.ts` gegen den Bestand ab).
 *
 * Quelle der Werte ist ausschliesslich `GET /api/users` (`Nutzer.lastLoginAt`/`lastSeenAt`, ISO-8601
 * in UTC). Alle Funktionen sind rein: "jetzt" kommt als Argument, nie aus `Date.now()` im Inneren -
 * so haengt keine Zusicherung an der Wanduhr, und der Aufrufer entscheidet, wann er die Uhr liest.
 */
import { fmtDT } from "./format.ts";

/** Wie weit zurueck ein Konto im Marken-Menue noch als "zuletzt aktiv" gilt. */
export const AKTIV_FENSTER_MS = 24 * 60 * 60 * 1000;

const MINUTE_MS = 60 * 1000;
const STUNDE_MS = 60 * MINUTE_MS;

/**
 * Der Zeitpunkt als deutsches Datum mit Uhrzeit in Ortszeit (`fmtDT`, dasselbe Format wie das
 * Aenderungsprotokoll derselben Seite), `—` bei `null`/leer/nicht lesbar.
 */
export function zeitpunktText(iso: string | null | undefined): string {
  if (!iso || Number.isNaN(Date.parse(iso))) return "—";
  return fmtDT(iso);
}

/**
 * Kurze Zeitangabe relativ zu `jetzt` (Millisekunden seit Epoche): unter einer Minute `gerade eben`,
 * unter einer Stunde `vor N Min.`, sonst `vor N Std.` - jeweils abgerundet. Ein Zeitstempel in der
 * Zukunft (Uhrenabweichung zwischen Server und Browser) zaehlt als `gerade eben`.
 */
export function relativeZeitangabe(iso: string, jetzt: number): string {
  const abstand = jetzt - Date.parse(iso);
  if (abstand < MINUTE_MS) return "gerade eben";
  if (abstand < STUNDE_MS) return `vor ${Math.floor(abstand / MINUTE_MS)} Min.`;
  return `vor ${Math.floor(abstand / STUNDE_MS)} Std.`;
}

/**
 * Die Konten, deren `lastSeenAt` weniger als `AKTIV_FENSTER_MS` vor `jetzt` liegt, zuletzt aktive
 * zuerst. Konten ohne (oder mit unlesbarem) `lastSeenAt` fallen heraus. Nicht nach `active`
 * gefiltert: ein deaktiviertes Konto verliert seine Sitzungen und faellt damit von selbst heraus.
 */
export function zuletztAktive<T extends { lastSeenAt?: string | null }>(
  konten: readonly T[],
  jetzt: number,
): Array<T & { lastSeenAt: string }> {
  return konten
    .map((konto) => ({ konto, gesehen: konto.lastSeenAt ? Date.parse(konto.lastSeenAt) : Number.NaN }))
    .filter(({ gesehen }) => !Number.isNaN(gesehen) && jetzt - gesehen < AKTIV_FENSTER_MS)
    .sort((a, b) => b.gesehen - a.gesehen)
    // Der Filter oben hat jedes Konto ohne lesbares `lastSeenAt` entfernt - der Typ zieht das nach.
    .map(({ konto }) => konto as T & { lastSeenAt: string });
}
